import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import {
  GUID,
  IElementCamView,
  IElementTypeHint,
  isIElementCamView,
  isIElementRoomSection,
  type IElementImg360,
} from "@faro-lotv/ielement-types";
import { clamp, SupportedCamera } from "@faro-lotv/lotv";
import {
  selectAncestor,
  selectBestViewForElement,
  selectChildDepthFirst,
  selectIElement,
  selectIElementWorldTransform,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { Quaternion, Vector3 } from "three";

/** Possible camera directions the user can choose from in the menu */
export const CAMERA_DIRECTIONS = [
  "top",
  "front",
  "right",
  "back",
  "left",
] as const;
export type CameraDirection = (typeof CAMERA_DIRECTIONS)[number];

export enum CameraViewType {
  /** Camera view with perspective projection */
  perspective = "perspective",

  /** Camera view with orthographic projection */
  orthographic = "orthographic",
}

/** Data describing the camera view. Unlike the IElementCamView, this object is in three.js coordinates. */
export type CameraView = {
  /** Camera type */
  type: CameraViewType;

  /** Position of the camera */
  position: Vector3;

  /** Rotation of the camera */
  rotation: Quaternion;

  /** FOV in degrees for a perspective camera, ortho vertical size for ortho camera */
  zoomLevel: IElementCamView["zoomLevel"];

  /** Optional rotation pivot for a given camera view */
  target?: Vector3;
};

export type SetCameraView = (view: CameraView) => void;

/**
 * @returns the best CameraView in three js coordinates for a given element
 * @param elementId the ID of the element to get the best view for
 */
export function selectBestCameraViewForElementInThreeSpace(elementId?: GUID) {
  return (state: OnlyIElementsStore): CameraView | undefined => {
    const element = selectIElement(elementId)(state);
    const savedViewForElement = selectBestViewForElement(element)(state);

    if (!savedViewForElement) return undefined;

    return selectCameraViewInThreeSpace(savedViewForElement)(state);
  };
}

/**
 * @returns the CameraView in three js coordinates for a given IElementCamView
 * @param state current app state
 * @param camView the IElementCamView to convert the CameraView for
 */
export const selectCameraViewInThreeSpace = curryAppSelector(
  createSelector(
    [
      (state: OnlyIElementsStore, camView: IElementCamView | undefined) =>
        selectIElementWorldTransform(camView?.id)(state),
      (state: OnlyIElementsStore, camView: IElementCamView | undefined) =>
        camView,
    ],
    (worldTransform, camView) => {
      if (!camView) return;

      const { position, quaternion } = worldTransform;
      return {
        type:
          camView.typeHint === IElementTypeHint.orthographic
            ? CameraViewType.orthographic
            : CameraViewType.perspective,
        position: new Vector3().fromArray(position),
        rotation: new Quaternion().fromArray(quaternion),
        zoomLevel: camView.zoomLevel,
      };
    },
  ),
);

/** Lower bound of the camera far plane setting (100m)*/
const FAR_PLANE_LOWER_BOUND = 100;

/** Upper bound of the camera far plane setting (10km)*/
const FAR_PLANE_UPPER_BOUND = 10000;

/**
 * Sets camera's far plane to the given value. The new far plane cannot be smaller than the previous,
 * furthermore the camera far plane is clamped within a specific range.
 *
 * @param camera The camera to set the far plane to
 * @param newFar The new value for far plane.
 */
export function setCameraFarPlane(
  camera: SupportedCamera,
  newFar: number,
): void {
  camera.far = clamp(
    Math.max(camera.far, newFar),
    FAR_PLANE_LOWER_BOUND,
    FAR_PLANE_UPPER_BOUND,
  );
}

/**
 * @returns The camera view of a pano if it exists
 * @param state The store containing the iElements reducer
 * @param iElement the pano IElement for which we want to extract the cam view
 */
export const selectPanoCamView = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, iElement: IElementImg360 | undefined) =>
        iElement,
    ],
    (iElements, iElement) => {
      const roomSection = selectAncestor(
        iElement,
        isIElementRoomSection,
      )({ iElements });
      const panoCamView = selectChildDepthFirst(
        roomSection,
        isIElementCamView,
      )({ iElements });
      const panoCamViewLookAt = selectCameraViewInThreeSpace(panoCamView)({
        iElements,
      });
      return panoCamViewLookAt;
    },
  ),
);
